import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  GetListsByJudicialDistrictGQL,
  Lawyer,
  LawyerComponentGQL,
  LawyerComponentQuery,
  LawyerInput,
  SaveLawyerGQL,
} from '../../../generated/graphql';
import { GraphQLError } from 'graphql';
import { AuthService } from '../../users/auth/auth.service';
import { NgForm } from '@angular/forms';

@Component({
  selector: 'app-lawyer',
  templateUrl: './lawyer.component.html',
  styleUrls: ['./lawyer.component.scss'],
})
export class LawyerComponent implements AfterViewInit {
  errors: ReadonlyArray<GraphQLError>;
  lawyer: LawyerComponentQuery['lawyer'] = new Lawyer();
  judicialDistricts: LawyerComponentQuery['judicialDistricts'] = [];

  availableLists: Array<{ id: number; name: string }> = [];
  selectedLists: Array<{ id: number; name: string }> = [];

  @ViewChild('form')
  form: NgForm;

  constructor(
    lawyerComponentService: LawyerComponentGQL,
    private saveLawyer: SaveLawyerGQL,
    private getListsByJudicialDistrictGQL: GetListsByJudicialDistrictGQL,
    private dialog: MatDialogRef<LawyerComponent>,
    public auth: AuthService,
    @Inject(MAT_DIALOG_DATA) data: any
  ) {
    lawyerComponentService.fetch({ id: data.id ?? 0 }, { fetchPolicy: 'network-only' }).subscribe(s => {
      // Apollo returns frozen objects — create a mutable copy so we can coerce date strings to Date
      const raw = s.data.lawyer;
      this.lawyer = raw
        ? {
            ...raw,
            temporalInactiveStart: raw.temporalInactiveStart ? new Date(raw.temporalInactiveStart as any) : null,
            temporalInactiveEnd: raw.temporalInactiveEnd ? new Date(raw.temporalInactiveEnd as any) : null,
          } as any
        : new Lawyer();
      this.judicialDistricts = s.data.judicialDistricts;

      // Task 4.7: preload selectedLists from lawyerListEntries in edit mode
      if (this.lawyer && this.lawyer.lawyerListEntries && this.lawyer.lawyerListEntries.length > 0) {
        this.selectedLists = this.lawyer.lawyerListEntries
          .filter(entry => entry && entry.list)
          .map(entry => ({ id: entry!.list.id, name: entry!.list.name }));

        // Detect judicial district from the first list and load availableLists
        const firstEntry = this.lawyer.lawyerListEntries.find(e => e && e.list && e.list.judicialDistrict);
        if (firstEntry) {
          const districtId = firstEntry.list.judicialDistrict.id;
          this.loadAvailableLists(districtId);
        }
      }
    });
  }

  private loadAvailableLists(districtId: number): void {
    this.getListsByJudicialDistrictGQL
      .fetch({ judicialDistrict: districtId })
      .subscribe(s => {
        const lists = s.data.getLawyersListsByJudicialDistrict;
        if (lists) {
          this.availableLists = lists
            .filter(l => l !== null)
            .map(l => ({ id: l!.id, name: l!.name }));
        } else {
          this.availableLists = [];
        }
      });
  }

  // Task 4.4: called when judicial district changes
  onJudicialDistrictChange(districtId: number): void {
    this.availableLists = [];
    if (districtId) {
      this.loadAvailableLists(districtId);
    }
  }

  // Task 4.5: add to selectedLists if not duplicate
  onListSelect(listId: number): void {
    if (!listId) return;
    const list = this.availableLists.find(l => l.id === listId);
    if (!list) return;
    const alreadySelected = this.selectedLists.some(l => l.id === listId);
    if (!alreadySelected) {
      this.selectedLists = [...this.selectedLists, list];
    }
  }

  // Task 4.6: remove from selectedLists
  onListRemove(list: { id: number; name: string }): void {
    this.selectedLists = this.selectedLists.filter(l => l.id !== list.id);
  }

  // Task 3.5: getter for date range validation
  get isDateRangeValid(): boolean {
    const start = this.lawyer?.temporalInactiveStart;
    const end = this.lawyer?.temporalInactiveEnd;
    if (!start || !end) return true;  // one or both null — backend handles partial dates
    return end >= start;
  }

  // Task 4.8 + Task 3.11: include selectedLists and handle temporal dates in the mutation
  save(lawyer: LawyerInput) {
    console.log('Saving lawyer with data:', lawyer);

      // Derive status and active from date range + today (dates-first model)
      const start = this.lawyer?.temporalInactiveStart;
      const end = this.lawyer?.temporalInactiveEnd;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let derivedStatus: string = (lawyer as any).status;
      let derivedActive: boolean = derivedStatus === 'ACTIVE';

      if (start && end) {
        const startDate = start instanceof Date ? start : new Date(start as any);
        const endDate = end instanceof Date ? end : new Date(end as any);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        if (today >= startDate && today <= endDate) {
          derivedStatus = 'TEMPORAL_INACTIVE';
          derivedActive = false;
        }
      }

      // map judicialDistrict to its id if the form binds an object
      const judicialDistrictId: number = (lawyer as any)?.judicialDistrict?.id ?? (lawyer as any)?.judicialDistrict ?? (this.lawyer?.judicialDistrict as any)?.id ?? 0;

      // build a sanitized input matching the GraphQL LawyerInput (avoid sending unexpected fields)
      const lawyerWithLists: LawyerInput = {
        name: (lawyer as any).name,
        lastName: (lawyer as any).lastName,
        email: (lawyer as any).email,
        identification: (lawyer as any).identification,
        lawyerLicense: (lawyer as any).lawyerLicense,
        phoneNumber: (lawyer as any).phoneNumber,
        blockUpdate: (lawyer as any).blockUpdate,
        status: derivedStatus as any,
        telegram: (lawyer as any).telegram,
        judicialDistrict: judicialDistrictId,
        selectedLists: this.selectedLists.length > 0 ? this.selectedLists.map(l => l.id) : undefined,
        active: derivedActive,
      };

      // log payload to help debugging server validation errors
      console.log('SaveLawyer payload:', { id: this.lawyer.id, lawyer: lawyerWithLists });

      // include temporal dates (format as yyyy-MM-dd using LOCAL time)
      const formatDate = (d: any): string | null => {
        if (!d) return null;
        // if already a string in YYYY-MM-DD, return as-is
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
        try {
          const parsed = d instanceof Date ? d : new Date(d);
          if (isNaN(parsed.getTime())) return null;
          // Use local time to avoid UTC offset shifting the date by one day
          const y = parsed.getFullYear();
          const m = String(parsed.getMonth() + 1).padStart(2, '0');
          const day = String(parsed.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        } catch (e) {
          return null;
        }
      };

      (lawyerWithLists as any).temporalInactiveStart = formatDate(this.lawyer?.temporalInactiveStart);
      (lawyerWithLists as any).temporalInactiveEnd = formatDate(this.lawyer?.temporalInactiveEnd);

      this.saveLawyer.mutate({ id: this.lawyer.id, lawyer: lawyerWithLists }).subscribe({
        next: s => {
          console.log('SaveLawyer response:', s);
          this.errors = s?.errors ?? [];
          if (s?.data?.saveLawyer) this.dialog.close();
        },
        error: err => {
          console.error('SaveLawyer error:', err);
          this.errors = err?.graphQLErrors ?? [err];
        },
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (!this.auth.lawyers) this.form.form.disable();
    }, 100);
  }
}
